import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { oids } from "@/configs/Settings";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { useSessionContext } from "@/contexts/SessionContext";

import {
  isSncrRetencaoTipo,
  SNCR_NOTIFICACAO_QTD,
} from "../constants";
import { parsePractitionerClassId } from "../helpers/parseClassId";
import { solicitarNumeracaoNotificacao } from "../services/sncrNotificacaoService";
import type { SncrConselho, SncrNotificacaoTipo } from "../types";
import { useSncrGovBrLogin } from "./useSncrGovBrLogin";

export type SncrNotificacaoForm = {
  receita: SncrNotificacaoTipo;
  conselho: SncrConselho;
  uf: string;
  documento: string;
  quantidade: number;
};

export function useSncrNotificacao() {
  const { isConnected, loginWithGovBr } = useSncrGovBrLogin();
  const { user, access } = useSessionContext();
  const { userPractitionerRole } = usePractitionerRoleContext();

  const defaults = useMemo(() => {
    const classId = userPractitionerRole?.identifier?.[0]?.value;
    const parsed = parsePractitionerClassId(classId);
    return {
      receita: "NRA" as SncrNotificacaoTipo,
      conselho: (parsed.conselho ?? "CRM") as SncrConselho,
      uf: parsed.uf ?? "",
      documento: parsed.documento ?? "",
      quantidade: SNCR_NOTIFICACAO_QTD.default,
    };
  }, [userPractitionerRole?.identifier]);

  const [form, setForm] = useState<SncrNotificacaoForm>(defaults);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      conselho: defaults.conselho || prev.conselho,
      uf: defaults.uf || prev.uf,
      documento: defaults.documento || prev.documento,
    }));
  }, [defaults.conselho, defaults.uf, defaults.documento]);

  const practitionerReference = user?.username
    ? `Practitioner/${oids.cpf}-${user.username}`
    : undefined;

  const organizationReference = access?.organizationId
    ? `Organization/${oids.cnpj}-${access.organizationId}`
    : undefined;

  async function solicitar() {
    if (!isConnected) {
      toast.info("Conecte ao SNCR (Gov.br) antes de solicitar numeração.");
      return;
    }

    setLoading(true);
    try {
      const result = await solicitarNumeracaoNotificacao({
        receita: form.receita,
        conselho: form.conselho,
        uf: form.uf.trim().toUpperCase(),
        documento: form.documento.replace(/\D/g, ""),
        quantidade: isSncrRetencaoTipo(form.receita)
          ? undefined
          : form.quantidade,
        practitionerReference,
        organizationReference,
      });

      toast.success(
        `Numeração salva: ${result.faixa.inicio} → ${result.faixa.fim} (${result.faixa.quantidade})`,
      );
      if (result.mensagem) {
        toast.info(result.mensagem);
      }
      return result;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Falha ao solicitar numeração SNCR.";
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    isConnected,
    loginWithGovBr,
    form,
    setForm,
    loading,
    solicitar,
  };
}
